import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  MessageCircle,
  MapPin,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  Inbox
} from "lucide-react";
import { useTranslation } from "react-i18next";
import BookingMessages from "../components/BookingMessages";

import { API } from "../utils/api";

export default function Messages() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  
  const [conversations, setConversations] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // "all", "owner", "tenant"

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user]);

  const fetchConversations = async () => {
    try {
      setLoadingMessages(true);
      const response = await axios.get(`${API}/messages/all`);
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const openConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessageModalOpen(true);
  };

  const handleModalClose = () => {
    setMessageModalOpen(false);
    // Refresh conversations to update unread counts
    fetchConversations();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('messages.now') || "Ahora";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const filteredConversations = conversations.filter(conv => {
    if (filter === "all") return true;
    return conv.user_role === filter;
  });

  const totalUnread = conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
  const ownerConversations = conversations.filter(c => c.user_role === "owner");
  const tenantConversations = conversations.filter(c => c.user_role === "tenant");

  if (authLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="pt-20 min-h-screen" data-testid="messages-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-violet-400" />
                {t('messages.title') || "Mensajes"}
                {totalUnread > 0 && (
                  <span className="px-2.5 py-1 bg-red-500 text-white text-sm font-medium rounded-full">
                    {totalUnread}
                  </span>
                )}
              </h1>
              <p className="text-gray-400">{t('messages.subtitle') || "Todas tus conversaciones con propietarios e inquilinos"}</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              filter === "all" 
                ? "bg-violet-500 text-white" 
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
            data-testid="filter-all"
          >
            {t('messages.all') || "Todos"} ({conversations.length})
          </button>
          <button
            onClick={() => setFilter("owner")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              filter === "owner" 
                ? "bg-emerald-500 text-white" 
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
            data-testid="filter-owner"
          >
            {t('profile.asOwner') || "Como propietario"} ({ownerConversations.length})
          </button>
          <button
            onClick={() => setFilter("tenant")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              filter === "tenant" 
                ? "bg-blue-500 text-white" 
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
            data-testid="filter-tenant"
          >
            {t('profile.asTenant') || "Como inquilino"} ({tenantConversations.length})
          </button>
        </div>

        {/* Conversations List */}
        {loadingMessages ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
          </div>
        ) : filteredConversations.length > 0 ? (
          <div className="space-y-3">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.booking_id}
                onClick={() => openConversation(conversation)}
                className={`dashboard-card p-4 cursor-pointer transition-all hover:border-violet-500/30 ${
                  conversation.unread_count > 0 ? "border-violet-500/20 bg-violet-500/5" : ""
                }`}
                data-testid={`conversation-${conversation.booking_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Spot Image */}
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/5">
                    <img
                      src={conversation.spot_image || "https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=100"}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <h3 className="text-white font-semibold truncate text-lg">
                          {conversation.other_participant}
                        </h3>
                        <p className="text-violet-400 text-sm truncate">
                          {conversation.spot_title}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="text-gray-500 text-sm">
                          {formatDate(conversation.last_message?.created_at)}
                        </span>
                        {conversation.unread_count > 0 && (
                          <span className="block mt-1 px-2.5 py-0.5 bg-violet-500 text-white text-xs font-medium rounded-full">
                            {conversation.unread_count} {t('messages.new') || "nuevo(s)"}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Last message preview */}
                    {conversation.last_message && (
                      <p className={`text-sm mt-2 line-clamp-2 ${
                        conversation.unread_count > 0 ? "text-white" : "text-gray-400"
                      }`}>
                        {conversation.last_message.sender_id === user?.id && (
                          <span className="text-violet-400">{t('messages.you') || "Tú"}: </span>
                        )}
                        {conversation.last_message.content}
                      </p>
                    )}
                    
                    {/* Role badge and location */}
                    <div className="flex items-center gap-3 mt-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        conversation.user_role === "owner" 
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                          : "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                      }`}>
                        {conversation.user_role === "owner" ? (t('profile.asOwner') || "Propietario") : (t('profile.asTenant') || "Inquilino")}
                      </span>
                      <span className="text-gray-500 text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {conversation.spot_address?.split(',')[0] || ""}
                      </span>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0 self-center" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="dashboard-card text-center py-16">
            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Inbox className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-3">{t('profile.noMessages') || "No tienes mensajes"}</h3>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              {t('profile.noMessagesDesc') || "Cuando tengas conversaciones con propietarios o inquilinos, aparecerán aquí"}
            </p>
            <Link to="/explore">
              <Button className="bg-violet-500 hover:bg-violet-600 text-white">
                <Search className="w-4 h-4 mr-2" />
                {t('explore.title') || "Explorar Plazas"}
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Messages Modal */}
      <Dialog open={messageModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[550px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-violet-400" />
              {t('messages.conversation') || "Conversación"}
            </DialogTitle>
            {selectedConversation && (
              <div className="mt-3 p-3 bg-[#15121e] rounded-lg border border-white/5">
                <p className="text-white font-medium">
                  {selectedConversation.spot_title}
                </p>
                <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {selectedConversation.spot_address}
                </p>
                <p className="text-violet-400 text-sm mt-1">
                  {selectedConversation.user_role === "owner" 
                    ? `${t('myBookings.client') || "Cliente"}: ${selectedConversation.other_participant}`
                    : `${t('spot.owner') || "Propietario"}: ${selectedConversation.other_participant}`
                  }
                </p>
              </div>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden mt-4">
            {selectedConversation && (
              <BookingMessages 
                bookingId={selectedConversation.booking_id}
                ownerName={selectedConversation.user_role === "owner" ? user?.name : selectedConversation.other_participant}
                tenantName={selectedConversation.user_role === "tenant" ? user?.name : selectedConversation.other_participant}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
